import React from "react";
import { Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useVisitorConsentContent } from "./visitor-consent-content.context";

export const VisitorConsentContentEditor: React.FC = () => {
  const { t } = useTranslation();
  const {
    draft,
    publishDraft,
    resetDraft,
    applyDraft,
    setDraftField,
    isEmptyDraft,
    isLoading,
    isPublishing
  } = useVisitorConsentContent();
  const titleEmpty = draft.title.trim() === "";
  const descriptionEmpty = draft.description.trim() === "";
  const isPublishDisabled = titleEmpty || descriptionEmpty;
  const handleUseDefaultContent = () => {
    applyDraft({
      title: t("_visitorConsent_title"),
      subtitle: t("_visitorConsent_pageDescription"),
      description: t("_visitorConsent_introBody")
    });
  };

  if (isLoading) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="visitor-consent-content-loading"
      >
        {t("Loading...")}
      </Typography>
    );
  }

  return (
    <Stack spacing={3} data-testid="visitor-consent-content-editor">
      <Typography variant="body1" color="text.secondary">
        {t("_visitorConsent_editorDescription")}
      </Typography>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Stack spacing={2} sx={{ flex: { xs: "1 1 auto", md: "6 1 0" }, minWidth: 0 }}>
          <TextField
            required
            label={t("_visitorConsent_editorFieldTitle")}
            value={draft.title}
            onChange={event => setDraftField("title", event.target.value)}
            fullWidth
            error={titleEmpty}
            helperText={titleEmpty ? t("_visitorConsent_editorTitleRequired") : undefined}
            data-testid="visitor-consent-editor-title"
          />
          <TextField
            label={t("_visitorConsent_editorFieldSubtitle")}
            value={draft.subtitle}
            onChange={event => setDraftField("subtitle", event.target.value)}
            fullWidth
            data-testid="visitor-consent-editor-subtitle"
          />
          <TextField
            required
            label={t("_visitorConsent_editorFieldDescription")}
            value={draft.description}
            onChange={event => setDraftField("description", event.target.value)}
            multiline
            minRows={6}
            fullWidth
            error={descriptionEmpty}
            helperText={
              descriptionEmpty ? t("_visitorConsent_editorDescriptionRequired") : undefined
            }
            data-testid="visitor-consent-editor-description"
          />

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={resetDraft}
              disabled={isEmptyDraft}
              data-testid="visitor-consent-editor-reset"
            >
              {t("_visitorConsent_editorReset")}
            </Button>
            <Button
              variant="outlined"
              onClick={handleUseDefaultContent}
              data-testid="visitor-consent-editor-use-default"
            >
              {t("_visitorConsent_editorUseDefault")}
            </Button>
            <Button
              variant="contained"
              onClick={publishDraft}
              disabled={isPublishDisabled || isPublishing}
              data-testid="visitor-consent-editor-publish"
            >
              {t("_visitorConsent_editorPublish")}
            </Button>
          </Stack>
        </Stack>

        <Paper
          variant="outlined"
          sx={{
            flex: { xs: "1 1 auto", md: "4 1 0" },
            minWidth: 0,
            p: 2,
            display: "flex",
            flexDirection: "column",
            minHeight: { xs: 280, md: 380 },
            position: "relative",
            bgcolor: "#fffef8",
            overflow: "hidden"
          }}
          data-testid="visitor-consent-editor-preview"
        >
          <Typography
            variant="h2"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) rotate(-38deg)",
              fontWeight: 800,
              letterSpacing: 2,
              color: "text.disabled",
              opacity: 0.18,
              userSelect: "none",
              pointerEvents: "none",
              whiteSpace: "nowrap"
            }}
            data-testid="visitor-consent-editor-preview-watermark"
          >
            {t("_visitorConsent_editorPreview")}
          </Typography>

          <Stack
            spacing={1}
            sx={{
              position: "relative",
              zIndex: 1,
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column"
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Typography variant="h6">{draft.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {draft.subtitle}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                  {draft.description}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                mt: "auto",
                flexShrink: 0,
                pt: 1.5,
                borderTop: "1px solid",
                borderColor: "divider"
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Button size="small" variant="outlined" disabled>
                  {t("_visitorConsent_previous")}
                </Button>
                <Button size="small" variant="outlined" disabled>
                  {t("_visitorConsent_next")}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
};
